import axios from 'axios';
import hmmMapMatching from './hmmMapMatching';

class TrackingService {
  constructor() {
    this.isTracking = false;
    this.currentPath = [];
    this.matchedPath = []; // HMM-matched path
    this.watchId = null;
    this.lastLocation = null;
    this.minDistance = 10; // meters - minimum distance between points
    this.blockDetectionRadius = 50; // meters - radius to consider path as "closed"
    this.currentTransportMode = 'walking';
    this.hmmMapMatching = hmmMapMatching;
    this.mapMatchingStats = null;
  }

  // Start GPS tracking
  startTracking(onLocationUpdate, onPathUpdate, transportMode = 'walking') {
    if (this.isTracking) return;
    
    this.isTracking = true;
    this.currentPath = [];
    this.lastLocation = null;
    this.currentTransportMode = transportMode;

    // Request high accuracy location updates
    const options = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 30000 // 30 seconds
    };

    this.watchId = navigator.geolocation.watchPosition(
      (position) => this.handleLocationUpdate(position, onLocationUpdate, onPathUpdate),
      (error) => console.error('GPS tracking error:', error),
      options
    );

    console.log('GPS tracking started');
  }

  // Stop GPS tracking
  stopTracking() {
    if (!this.isTracking) return;

    if (this.watchId) {
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
    }

    this.isTracking = false;
    console.log('GPS tracking stopped');
    
    // Return the final path for processing
    return this.currentPath;
  }

  // Handle new location updates
  handleLocationUpdate(position, onLocationUpdate, onPathUpdate) {
    const newLocation = {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      timestamp: position.timestamp,
      accuracy: position.coords.accuracy,
      transportMode: this.currentTransportMode
    };

    // Check if this point is far enough from the last point
    if (this.shouldAddPoint(newLocation)) {
      this.currentPath.push(newLocation);
      this.lastLocation = newLocation;
      
      // Check if we've completed a block
      const blockInfo = this.detectCompletedBlock();
      
      // Notify components of updates
      if (onLocationUpdate) onLocationUpdate(newLocation);
      if (onPathUpdate) onPathUpdate(this.currentPath, blockInfo);
    }
  }

  // Check if a new point should be added to the path
  shouldAddPoint(newLocation) {
    if (!this.lastLocation) return true;

    const distance = this.calculateDistance(
      this.lastLocation.latitude,
      this.lastLocation.longitude,
      newLocation.latitude,
      newLocation.longitude
    );

    return distance >= this.minDistance;
  }

  // Calculate distance between two points in meters
  calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lon2 - lon1) * Math.PI / 180;

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  }

  // Detect if the current path forms a completed block
  detectCompletedBlock() {
    if (this.currentPath.length < 4) return null; // Need at least 4 points

    const startPoint = this.currentPath[0];
    const endPoint = this.currentPath[this.currentPath.length - 1];
    
    // Check if we're close to the starting point
    const distanceToStart = this.calculateDistance(
      startPoint.latitude,
      startPoint.longitude,
      endPoint.latitude,
      endPoint.longitude
    );

    if (distanceToStart <= this.blockDetectionRadius) {
      // We have a potential block - analyze it
      return this.analyzeBlock();
    }

    return null;
  }

  // Analyze if the block should be marked (no unwalked internal roads)
  async analyzeBlock() {
    if (this.currentPath.length < 4) return null;

    try {
      // Get the bounding box of the path
      const bounds = this.getPathBounds();
      
      // Get all roads within the enclosed area
      const internalRoads = await this.getRoadsInArea(bounds);
      
      // Check if there are unwalked internal roads
      const unwalkedRoads = this.findUnwalkedInternalRoads(internalRoads);
      
      const blockInfo = {
        isCompleteBlock: true,
        shouldMarkArea: unwalkedRoads.length === 0,
        unwalkedRoadsCount: unwalkedRoads.length,
        totalDistance: this.calculateTotalDistance(),
        area: this.calculatePathArea(),
        path: [...this.currentPath]
      };

      return blockInfo;
    } catch (error) {
      console.error('Error analyzing block:', error);
      return null;
    }
  }

  // Get bounding box of the current path
  getPathBounds() {
    const lats = this.currentPath.map(p => p.latitude);
    const lons = this.currentPath.map(p => p.longitude);
    
    return {
      minLat: Math.min(...lats),
      maxLat: Math.max(...lats),
      minLon: Math.min(...lons),
      maxLon: Math.max(...lons)
    };
  }

  // Get roads within the specified area using Mapbox
  async getRoadsInArea(bounds) {
    const { minLat, maxLat, minLon, maxLon } = bounds;
    
    // Use Mapbox Vector Tiles to get road data
    // This is a simplified version - in production you'd use proper vector tile parsing
    const bbox = `${minLon},${minLat},${maxLon},${maxLat}`;
    
    try {
      // For now, we'll use a simplified approach
      // In a full implementation, you'd parse Mapbox vector tiles
      const roads = await this.fetchRoadsFromMapbox(bbox);
      return roads;
    } catch (error) {
      console.error('Error fetching roads:', error);
      return [];
    }
  }

  // Simplified road fetching (placeholder for now)
  async fetchRoadsFromMapbox(bbox) {
    // This would normally parse Mapbox vector tiles
    // For now, return empty array - we'll implement this next
    return [];
  }

  // Find roads within the area that haven't been walked
  findUnwalkedInternalRoads(roads) {
    // This is a simplified version
    // In production, you'd do proper geometric analysis
    return roads.filter(road => {
      // Check if any part of this road is within the path area
      // and hasn't been walked
      return !this.isRoadWalked(road);
    });
  }

  // Check if a road has been walked (simplified)
  isRoadWalked(road) {
    // Simplified check - in production you'd do proper path intersection
    return false; // Placeholder
  }

  // Calculate total distance of the path
  calculateTotalDistance() {
    let totalDistance = 0;
    
    for (let i = 1; i < this.currentPath.length; i++) {
      const prev = this.currentPath[i - 1];
      const curr = this.currentPath[i];
      
      totalDistance += this.calculateDistance(
        prev.latitude,
        prev.longitude,
        curr.latitude,
        curr.longitude
      );
    }
    
    return totalDistance / 1000; // Convert to kilometers
  }

  // Calculate approximate area of the path (simplified)
  calculatePathArea() {
    // This is a simplified area calculation
    // In production, you'd use proper geometric algorithms
    const bounds = this.getPathBounds();
    const width = this.calculateDistance(
      bounds.minLat, bounds.minLon,
      bounds.minLat, bounds.maxLon
    );
    const height = this.calculateDistance(
      bounds.minLat, bounds.minLon,
      bounds.maxLat, bounds.minLon
    );
    
    return (width * height) / 1000000; // Convert to km²
  }

  // Perform HMM map matching on current path
  async performMapMatching() {
    if (this.currentPath.length === 0) {
      console.log('No GPS path available for map matching');
      return null;
    }

    try {
      console.log(`Starting HMM map matching for ${this.currentPath.length} GPS points...`);
      
      const result = await this.hmmMapMatching.matchPath(this.currentPath);
      
      this.matchedPath = result.matchedPath;
      this.mapMatchingStats = result.stats;
      
      console.log(`Map matching complete: ${result.stats.matchRate.toFixed(1)}% match rate`);
      return result;
    } catch (error) {
      console.error('Map matching failed:', error);
      return null;
    }
  }

  // Save tracking session to backend with HMM map matching
  async saveTrackingSession(blockInfo) {
    try {
      // Perform HMM map matching if not already done
      if (this.matchedPath.length === 0) {
        await this.performMapMatching();
      }

      const sessionData = {
        path: this.currentPath, // Original GPS path
        matchedPath: this.matchedPath, // HMM-matched path
        totalDistance: blockInfo?.totalDistance || this.calculateTotalDistance(),
        duration: this.getSessionDuration(),
        isCompleteBlock: blockInfo?.isCompleteBlock || false,
        shouldMarkArea: blockInfo?.shouldMarkArea || false,
        unwalkedRoadsCount: blockInfo?.unwalkedRoadsCount || 0,
        transportMode: this.currentTransportMode,
        mapMatchingStats: this.mapMatchingStats
      };

      const response = await axios.post('/api/gps/session', sessionData);
      return response.data;
    } catch (error) {
      console.error('Error saving tracking session:', error);
      throw error;
    }
  }

  // Get session duration in seconds
  getSessionDuration() {
    if (this.currentPath.length < 2) return 0;
    
    const startTime = this.currentPath[0].timestamp;
    const endTime = this.currentPath[this.currentPath.length - 1].timestamp;
    
    return (endTime - startTime) / 1000; // Convert to seconds
  }

  // Get current tracking status
  getTrackingStatus() {
    return {
      isTracking: this.isTracking,
      pathLength: this.currentPath.length,
      totalDistance: this.calculateTotalDistance(),
      lastLocation: this.lastLocation,
      hasMatchedPath: this.matchedPath.length > 0,
      mapMatchingStats: this.mapMatchingStats
    };
  }

  // Get matched path (HMM result)
  getMatchedPath() {
    return this.matchedPath;
  }

  // Get map matching statistics
  getMapMatchingStats() {
    return this.mapMatchingStats;
  }

  // Clear matched path data
  clearMatchedPath() {
    this.matchedPath = [];
    this.mapMatchingStats = null;
  }

  // Clear current path
  clearPath() {
    this.currentPath = [];
    this.lastLocation = null;
  }
}

export default new TrackingService();
